import type { ComponentProps, ReactElement } from 'react';

import type {
    CollapsedSubcategoryPlaceholder,
    EmptySubcategoryPlaceholder,
    NonValueOption,
    ValueType,
} from './utils';

import MenuList from '@mui/material/MenuList';

import { isExtendedMaterialOnTheFly, isMaterialOnTheFly } from '../../utils';

import MenuItemMaterial from './MenuItemMaterial';
import MenuItemMaterialOnTheFly from './MenuItemMaterialOnTheFly';

interface MaterialValueProps {
    /** The material or MOF object */
    value: Exclude<
        ValueType,
        | CollapsedSubcategoryPlaceholder
        | EmptySubcategoryPlaceholder
        | NonValueOption
    >;
    /** Whether to render the value as disabled */
    isDisabled?: boolean;
    /** Whether to render the value as static (hide edit functionality for MOF) */
    isStatic?: boolean;
    /** Whether the material image is loading or not */
    isImageLoading: boolean;
    /** Any additional props for the MenuItemMaterial component */
    menuItemMaterialProps?: ComponentProps<typeof MenuItemMaterial>;
    /** Any additional props for the MenuItemMaterialOnTheFly component */
    menuItemMaterialOnTheFlyProps?: ComponentProps<
        typeof MenuItemMaterialOnTheFly
    >;
}

const MaterialValue = ({
    value,
    isImageLoading,
    isStatic = false,
    isDisabled = false,
    menuItemMaterialProps,
    menuItemMaterialOnTheFlyProps,
}: MaterialValueProps): ReactElement => {
    if (!value) return <span />;

    const sx =
        isStatic || isDisabled
            ? { ['pointer-events']: 'none !important' }
            : undefined;

    if (isMaterialOnTheFly(value) || isExtendedMaterialOnTheFly(value))
        return (
            <MenuList
                sx={{ display: 'contents' }}
                disablePadding
            >
                <MenuItemMaterialOnTheFly
                    {...menuItemMaterialOnTheFlyProps}
                    sx={sx}
                    mof={value}
                    disabled={isDisabled}
                    disableEdit={isStatic}
                />
            </MenuList>
        );

    return (
        <MenuList
            sx={{ display: 'contents' }}
            disablePadding
        >
            <MenuItemMaterial
                {...menuItemMaterialProps}
                material={value}
                sx={sx}
                isImageLoading={isImageLoading}
                disabled={isDisabled}
                disableRipple
                disableTouchRipple
            />
        </MenuList>
    );
};

export { MaterialValue };
